import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import userAgentAccessTokenService, {
  buildMcpSnippet,
  UserAgentAccessTokenCreated,
  UserAgentAccessTokenListItem,
} from '../../services/userAgentAccessTokenService';
import { getApiUrl } from '../../config/appConfig';

function defaultExpiresLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const AgentAccessTokens: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<UserAgentAccessTokenListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiresLocal, setExpiresLocal] = useState(defaultExpiresLocal);
  const [created, setCreated] = useState<UserAgentAccessTokenCreated | null>(null);
  const [saving, setSaving] = useState(false);

  const mcpSnippet = useMemo(
    () => buildMcpSnippet(created?.token ?? `\${NEOS_AGENT_TOKEN}`),
    [created],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await userAgentAccessTokenService.list());
    } catch (e: unknown) {
      console.error(e);
      enqueueSnackbar('Error al cargar claves de agente', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleOpenCreate = () => {
    setName('');
    setExpiresLocal(defaultExpiresLocal());
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      enqueueSnackbar('Indicá un nombre para la clave', { variant: 'warning' });
      return;
    }
    const expiresAt = new Date(expiresLocal);
    if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
      enqueueSnackbar('La fecha de vencimiento debe ser futura', { variant: 'warning' });
      return;
    }
    const max = Date.now() + 90 * 24 * 60 * 60 * 1000;
    if (expiresAt.getTime() > max) {
      enqueueSnackbar('El vencimiento no puede superar 90 días', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const result = await userAgentAccessTokenService.create({
        name: name.trim(),
        expiresAt: expiresAt.toISOString(),
      });
      setCreateOpen(false);
      setCreated(result);
      enqueueSnackbar('Clave generada. Copiala ahora: no se volverá a mostrar.', { variant: 'success' });
      await load();
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'No se pudo crear la clave';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (row: UserAgentAccessTokenListItem) => {
    if (!window.confirm(`¿Revocar la clave «${row.name}»?`)) return;
    try {
      await userAgentAccessTokenService.revoke(row.id);
      enqueueSnackbar('Clave revocada', { variant: 'success' });
      await load();
    } catch (e: unknown) {
      console.error(e);
      enqueueSnackbar('No se pudo revocar la clave', { variant: 'error' });
    }
  };

  const copyText = async (text: string, okMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar(okMsg, { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar al portapapeles', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Claves de agente (LLM)
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Nueva clave
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generá una clave personal para OpenClaw, Claude Cowork, DeepSeek u otros agentes. Actúa con tu usuario,
            tus empresas (tenants) y tus permisos. Vencimiento máximo: 90 días. El Bearer solo se muestra una vez.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            MCP: <Box component="span" sx={{ fontFamily: 'monospace' }}>{getApiUrl().replace(/\/$/, '')}/mcp</Box>
            {' · '}REST con el mismo Bearer contra la API. Header opcional{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>X-Tenant-Id</Box> para cambiar de empresa.
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Creada</TableCell>
                    <TableCell>Vence</TableCell>
                    <TableCell>Último uso</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          Todavía no tenés claves. Creá una para conectar un agente.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
                        <TableCell>{new Date(row.expiresAt).toLocaleString()}</TableCell>
                        <TableCell>
                          {row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>
                          {row.isRevoked
                            ? 'Revocada'
                            : new Date(row.expiresAt).getTime() <= Date.now()
                              ? 'Vencida'
                              : 'Vigente'}
                        </TableCell>
                        <TableCell align="right">
                          {!row.isRevoked && (
                            <Tooltip title="Revocar">
                              <IconButton size="small" color="error" onClick={() => void handleRevoke(row)}>
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva clave de agente</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              placeholder="Ej. OpenClaw laptop"
              helperText="Solo para identificarla; no afecta permisos."
            />
            <TextField
              label="Vence"
              type="datetime-local"
              value={expiresLocal}
              onChange={(e) => setExpiresLocal(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Máximo 90 días desde ahora."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleCreate()} disabled={saving}>
            {saving ? 'Generando…' : 'Generar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(created)} onClose={() => setCreated(null)} maxWidth="md" fullWidth>
        <DialogTitle>Clave generada (guardala ahora)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Este valor no se volverá a mostrar. Copiá el Bearer y guardalo en un lugar seguro (variable de entorno).
          </Typography>
          {created && (
            <Stack spacing={2}>
              <TextField
                label="Bearer (JWT)"
                value={created.token}
                fullWidth
                multiline
                minRows={3}
                InputProps={{ readOnly: true }}
              />
              <Button
                startIcon={<ContentCopyIcon />}
                variant="outlined"
                onClick={() => void copyText(created.token, 'Bearer copiado')}
              >
                Copiar Bearer
              </Button>
              <Typography variant="subtitle2">Snippet OpenClaw / MCP</Typography>
              <TextField
                value={mcpSnippet}
                fullWidth
                multiline
                minRows={8}
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <Button
                startIcon={<ContentCopyIcon />}
                variant="outlined"
                onClick={() => void copyText(mcpSnippet, 'Snippet MCP copiado')}
              >
                Copiar snippet MCP
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreated(null)} variant="contained">
            Listo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentAccessTokens;
